import { makeStyles } from '@material-ui/core/styles';
import { HEADER_HEIGHT, SecondaryColors, TextColors, TextOpacities } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const getLightTheme = (muiTheme: any) => ({
  root: {
    '& $content': {
      color: TextColors.dark,
    },
    '& $tabs': {
      [muiTheme.breakpoints.down('sm')]: {
        '&:before': {
          borderBottom: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.dark,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.dark, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.dark,
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.light2),

        '&.Mui-selected': {
          color: TextColors.dark,
        },

        [muiTheme.breakpoints.up('md')]: {
          '&:hover': {
            color: TextColors.dark,
          },
        },
      },
    },
  },
});

const getDarkTheme = (muiTheme: any) => ({
  root: {
    '& $content': {
      color: TextColors.light,
    },
    '& $tabs': {
      [muiTheme.breakpoints.down('sm')]: {
        '&:before': {
          borderBottom: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.main,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.light, TextOpacities.dark2),

      '& b, & strong': {
        color: TextColors.light,
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.dark2),

        '&.Mui-selected': {
          color: TextColors.light,
        },

        [muiTheme.breakpoints.up('md')]: {
          '&:hover': {
            color: TextColors.light,
          },
        },
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': getLightTheme(muiTheme).root,
      '&.dark-theme': getDarkTheme(muiTheme).root,
      height: '100%',

      [muiTheme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
      },

      '&.is-sticky': {
        [muiTheme.breakpoints.up('md')]: {
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          height: 'auto',
        },
      },
    },
    container: {
      '& > div[data-testid="qa-container"]': {
        [muiTheme.breakpoints.up('md')]: {
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
        },
      },
    },
    ghostBlock: {
      height: '50vh',

      '&:last-child': {
        height: '100vh',
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      justifyContent: 'center',

      [muiTheme.breakpoints.up('md')]: {
        display: 'block',
        height: 'auto',
        width: '100%',
      },

      '& .tabPanels': {
        marginBottom: '28px',
      },
    },
    tabs: {
      '&.MuiTabs-root': {
        position: 'relative',
        display: 'inline-flex',

        [muiTheme.breakpoints.down('sm')]: {
          margin: '-2px -2px 0px -32px',
          width: 'calc(100% + 64px)',

          '& .MuiTabs-scrollable': {
            padding: '2px 2px 0px 2px',
            margin: '-2px -2px 0 -2px',
          },

          '&:before': {
            content: '""',
            width: 'calc(100% - 64px)',
            position: 'absolute',
            top: 0,
            left: '32px',
            bottom: 0,
          },

          '& .MuiTabs-scrollButtons': {
            width: '32px',
          },

          '& + *': {
            marginTop: '28px',
          },
        },

        [muiTheme.breakpoints.up('md')]: {
          margin: '0',
          minHeight: '96px',
          maxWidth: '100%',
          width: '100%',

          '& .MuiTabs-flexContainer': {
            position: 'relative',
            width: '100%',
            justifyContent: 'space-evenly',
          },

          '& .MuiTabs-scrollable': {
            display: 'inline-flex',
            justifyContent: 'center',
          },

          '& .MuiTabs-scroller': {
            margin: '16px 0 !important',
          },
        },
      },
    },
    tab: {
      '&.MuiTab-root': {
        '& img': {
          maxHeight: '40px',
        },
      },
      '& .MuiTypography-body1': {
        lineHeight: '2em',
        marginBottom: muiTheme.spacing(0.5),

        [muiTheme.breakpoints.up('md')]: {
          marginBottom: muiTheme.spacing(1.5),
        },
      },

      '&.MuiTab-textColorInherit': {
        opacity: 1,
      },
    },
    tabPanelContent: {
      '& > * + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    tabPanelAnimated: {
      '& > *': {
        animation: 'enter 250ms ease-out',
      },
    },
    tabPanel: {
      position: 'relative',
      flex: '0 0 0 !important',

      '&': {
        [muiTheme.breakpoints.up('md')]: {
          width: '53%',
          flex: 1,
          display: 'flex',
          flexWrap: 'nowrap',
          overflow: 'hidden',
          flexShrink: '0 !important',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
        },
      },
      '& [role="tabpanel"]': {
        [muiTheme.breakpoints.up('md')]: {
          padding: muiTheme.spacing(10, 12),
          width: '100%',
          flexShrink: 0,
        },
      },
    },
    superheading: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(2),
      },
    },
    heading: {
      '&, &.MuiTypography-root': {
        marginTop: 0,
      },
    },
    headingRTC: {
      '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
        ...muiTheme.typography.h4,
      },

      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    rtc: {},
    link: {
      '&:not(:first-child)': {
        marginTop: muiTheme.spacing(4),

        [muiTheme.breakpoints.down('sm')]: {
          width: '100%',
        },
      },
    },
  }),
  { name: 'GenCTLHorizontalTopContent' },
);

export default useStyles;
